import React, { useEffect, useState } from 'react';
import { CopyIcon, CheckIcon, TerminalIcon } from 'lucide-react';
interface Command {
  input: string;
  output: string[];
  delay?: number;
}
interface TerminalProps {
  commands: Command[];
  className?: string;
}
export function Terminal({
  commands,
  className = ''
}: TerminalProps) {
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState(0);
  const [typedCommand, setTypedCommand] = useState('');
  const [showOutput, setShowOutput] = useState(false);
  useEffect(() => {
    if (step >= commands.length) return;
    const currentCmd = commands[step];
    if (typedCommand.length < currentCmd.input.length) {
      const timeout = setTimeout(() => {
        setTypedCommand(currentCmd.input.slice(0, typedCommand.length + 1));
      }, 50); // Typing speed
      return () => clearTimeout(timeout);
    } else if (!showOutput) {
      const timeout = setTimeout(() => {
        setShowOutput(true);
      }, 400); // Pause before showing output
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setStep(s => s + 1);
        setTypedCommand('');
        setShowOutput(false);
      }, currentCmd.delay || 2000); // Time before next command
      return () => clearTimeout(timeout);
    }
  }, [step, typedCommand, showOutput, commands]);
  const handleCopy = () => {
    const textToCopy = commands.map(c => c.input).join('\n');
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className={`rounded-xl overflow-hidden border border-pk-code-border bg-pk-code-bg shadow-lg ${className}`} data-id="element-198">
      {/* Window Chrome */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-pk-code-border bg-[#161b22]" data-id="element-199">
        <div className="flex items-center gap-2" data-id="element-200">
          <div className="w-3 h-3 rounded-full bg-pk-error/80" data-id="element-201"></div>
          <div className="w-3 h-3 rounded-full bg-pk-amber/80" data-id="element-202"></div>
          <div className="w-3 h-3 rounded-full bg-pk-success/80" data-id="element-203"></div>
        </div>
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground font-mono" data-id="element-204">
          <TerminalIcon className="w-3.5 h-3.5" data-id="element-205" />
          bash
        </div>
        <button onClick={handleCopy} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Copy commands" data-id="element-206">
          {copied ? <CheckIcon className="w-4 h-4 text-pk-success" data-id="element-207" /> : <CopyIcon className="w-4 h-4" data-id="element-208" />}
        </button>
      </div>

      {/* Terminal Content */}
      <div className="p-4 font-mono text-sm overflow-x-auto min-h-[200px] text-foreground/90" data-id="element-209">
        {commands.slice(0, step).map((cmd, i) => <div key={i} className="mb-4" data-id="element-210">
            <div className="flex items-center gap-2 text-pk-info-dark" data-id="element-211">
              <span className="text-pk-success" data-id="element-212">➜</span>
              <span className="text-pk-teal" data-id="element-213">~</span>
              <span className="text-foreground" data-id="element-214">{cmd.input}</span>
            </div>
            <div className="mt-1 space-y-1" data-id="element-215">
              {cmd.output.map((line, j) => <div key={j} className={`${line.startsWith('✔') ? 'text-pk-success' : line.startsWith('⚠') ? 'text-pk-amber' : 'text-muted-foreground'}`} data-id="element-216">
                  {line}
                </div>)}
            </div>
          </div>)}

        {step < commands.length && <div data-id="element-217">
            <div className="flex items-center gap-2 text-pk-info-dark" data-id="element-218">
              <span className="text-pk-success" data-id="element-219">➜</span>
              <span className="text-pk-teal" data-id="element-220">~</span>
              <span className="text-foreground" data-id="element-221">
                {typedCommand}
                <span className="inline-block w-2 h-4 bg-foreground ml-1 animate-caret-blink align-middle" data-id="element-222"></span>
              </span>
            </div>
            {showOutput && <div className="mt-1 space-y-1" data-id="element-223">
                {commands[step].output.map((line, j) => <div key={j} className={`${line.startsWith('✔') ? 'text-pk-success' : line.startsWith('⚠') ? 'text-pk-amber' : 'text-muted-foreground'}`} data-id="element-224">
                    {line}
                  </div>)}
              </div>}
          </div>}
      </div>
    </div>;
}